import { isClient } from './isClient';
import { INTERNAL_QUERY_PARAMS_LIST } from '../constants';

export const getUrlAddress = () => (isClient ? window.location.href : null);

export const tryGetQueryParam = (param, url = getUrlAddress()) => {
  const cleanParam = param?.replace(/[[\]]/g, '\\$&');
  const regex = new RegExp(`[?&]${cleanParam}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

/**
 * Extracts a parameter that might be embedded in a malformed query string.
 * This handles cases like: ?instoreapp%2Fpath%3FstoreNumber=5604
 * where storeNumber is embedded inside the instoreapp value after an encoded ?
 * @param {String} param - The parameter name to extract
 * @param {String} url - The URL to check (defaults to current URL)
 * @returns {String|null} The parameter value if found, null otherwise
 */
export const tryGetEmbeddedQueryParam = (param, url = getUrlAddress()) => {
  if (!url) return null;

  try {
    // First try the standard approach
    const standardResult = tryGetQueryParam(param, url);
    if (standardResult !== null && standardResult !== '') {
      return standardResult;
    }

    // Look for the param embedded in the query string (after %3F or ?)
    // Pattern: param=value where it might be after encoded ? or real ?
    const pattern = new RegExp(`[?&]${param}=([^&]*)`, 'i');

    // Decode the URL to find embedded params
    const decodedUrl = decodeURIComponent(url);
    const match = pattern.exec(decodedUrl);
    if (match && match[1]) {
      return decodeURIComponent(match[1]);
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Checks if a query parameter exists in the URL (even if malformed)
 * @param {String} param - The parameter name to check
 * @param {String} url - The URL to check (defaults to current URL)
 * @returns {Boolean} True if parameter exists, false otherwise
 */
export const hasQueryParam = (param, url = getUrlAddress()) => {
  if (!url) return false;

  // Try the typical way first
  const standardResult = tryGetQueryParam(param, url);
  if (standardResult !== null) return true;

  // check for malformed cases (e.g., ?param without =value)
  try {
    const urlObj = new URL(url);
    const searchString = urlObj.search.toLowerCase();
    return searchString.includes(param.toLowerCase());
  } catch (error) {
    return false;
  }
};

export const setQueryParam = (key, val, navigate, replace) => {
  if (isClient) {
    const url = new URL(window.location);
    url.searchParams.set(key, val);
    if (navigate) {
      const replaceOriginal = (replace === undefined || replace === null) ? true : replace;
      return navigate({ search: url.search }, { replace: replaceOriginal });
    }

    window.history.replaceState('add design id', document.title, url);
    return null;
  }
  return null;
};

export const deleteQueryParam = (key) => {
  if (isClient) {
    const url = new URL(window.location);
    url.searchParams.delete(key);
    return window.history.replaceState('remove design id', document.title, url);
  }

  return null;
};

// Utility to get a query param from the URL, case-insensitive
export const getQueryParamCI = (paramName) => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  let result = null;
  params.forEach((value, key) => {
    if (key.toLowerCase() === paramName.toLowerCase()) {
      result = value;
    }
  });
  return result;
};

/**
 * Generates a shareable URL by removing internal query parameters.
 * @param {String} url - The URL string to sanitize. Defaults to current window URL.
 * @returns {String} Sanitized URL string without internal query parameters
 */
export const getShareableUrl = (url = getUrlAddress()) => {
  if (!isClient || !url) return null;
  try {
    const newDecodedURL = decodeURIComponent(url);
    const urlObj = new URL(newDecodedURL);
    const keysToDelete = [];

    urlObj.searchParams.forEach((value, key) => {
      // Check if this key contains or starts with any internal param name
      const shouldDelete = INTERNAL_QUERY_PARAMS_LIST.some((internalParam) => {
        const lowerKey = key.toLowerCase();
        const lowerInternalParam = internalParam.toLowerCase();
        // Match if key starts with internal param or contains it
        // This catches: "storeNumber", "instoreapp%/designs/...?storeNumber", etc.
        return lowerKey === lowerInternalParam || lowerKey.includes(lowerInternalParam);
      });

      if (shouldDelete) {
        keysToDelete.push(key);
      }
    });

    // Delete all matched keys
    keysToDelete.forEach((key) => {
      urlObj.searchParams.delete(key);
    });

    return urlObj.toString();
  } catch (error) {
    return null;
  }
};

/**
 * Extracts the product ID from the URL path (last numeric segment)
 * @param {String} url - The URL to extract from (defaults to current URL)
 * @returns {String|null} The product ID if found, null otherwise
 */
export const getProductIdFromPath = (url = getUrlAddress()) => {
  if (!url) return null;

  try {
    const urlObj = new URL(url);
    const pathSegments = urlObj.pathname.split('/').filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1];

    // Check if last segment is numeric (product ID)
    if (/^\d+$/.test(lastSegment)) {
      return lastSegment;
    }
    return null;
  } catch (error) {
    return null;
  }
};